import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import OfferDialog from "./OfferDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useForm } from "react-hook-form";
import { useAxiosInstance } from "../../services/Interceptor";
import { getOfferAcceptanceStatus } from "../../services/AthleteService";
import { SaveOfferAcceptanceStatus } from "../../services/EventsService";
import { toast } from "react-toastify";

function OfferAcceptanceModal({ open, onClose, onSubmit, initialValues }) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { offerAcceptanceStatus: "no", offerAcceptanceSchool: "" },
  });

  useEffect(() => {
    if (open) {
      reset({
        offerAcceptanceStatus: initialValues?.offerAcceptanceStatus || "no",
        offerAcceptanceSchool: initialValues?.offerAcceptanceSchool || "",
      });
    }
  }, [open, initialValues, reset]);

  const offerAcceptanceStatus = watch("offerAcceptanceStatus");

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[350]">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="w-full max-w-2xl bg-white rounded-lg shadow-xl">
          <div className="p-6">
            <DialogTitle className="text-lg font-bold mb-4">
              Offer Acceptance Details
            </DialogTitle>
            <form
              onSubmit={handleSubmit((data) =>
                onSubmit(
                  data.offerAcceptanceStatus,
                  data.offerAcceptanceSchool,
                ),
              )}
            >
              <div className="mt-6 flex gap-4 items-end">
                <div className="form-field flex-1">
                  <select
                    id="offerAcceptanceStatus"
                    {...register("offerAcceptanceStatus")}
                    className="bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat text-gray-900 w-full p-2"
                  >
                    <option value="no">No</option>
                    <option value="Committed">
                      I have made a verbal commitment
                    </option>
                    <option value="Signed">
                      I have signed a letter of intent
                    </option>
                  </select>
                </div>
                <div className="form-field flex-1">
                  <input
                    type="text"
                    id="offerAcceptanceSchool"
                    placeholder="School name"
                    disabled={offerAcceptanceStatus === "no"}
                    {...register("offerAcceptanceSchool", {
                      validate: (v) =>
                        offerAcceptanceStatus === "no" ||
                        !!v?.trim() ||
                        "School is required",
                    })}
                    className={`border rounded p-2 w-full ${
                      offerAcceptanceStatus === "no"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  />
                  {errors.offerAcceptanceSchool && (
                    <span className="text-red-500 text-sm">
                      {errors.offerAcceptanceSchool.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 border rounded"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default function AthleteOffersTab({
  athleteId,
  offers,
  openCreateOffer,
  openEditOffer,
  openDeleteOffer,
  isOfferOpen,
  setIsOfferOpen,
  isOfferEdit,
  handleOfferSubmit,
  handleOfferSubmission,
  offerRegister,
  divisions,
  date,
  loading,
  resetOffer,
  setSelectedOffer,
  setIsOfferEdit,
  deleteOfferOpen,
  setDeleteOfferOpen,
  handleOfferDeleteConfirm,
  onAcceptanceChange,
}) {
  const apiInstance = useAxiosInstance();
  const [savedStatus, setSavedStatus] = useState("");
  const [savedSchool, setSavedSchool] = useState("");
  const [acceptanceLoaded, setAcceptanceLoaded] = useState(false);
  const [acceptanceModalOpen, setAcceptanceModalOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [savingAcceptance, setSavingAcceptance] = useState(false);

  // Derived — avoids stale boolean state diverging from savedStatus
  const hasAcceptedOffer = ["Committed", "Signed"].includes(savedStatus);

  const loadAcceptanceStatus = useCallback(() => {
    if (!athleteId) return;
    setAcceptanceLoaded(false);
    getOfferAcceptanceStatus(apiInstance, athleteId)
      .then((data) => {
        setSavedStatus(data?.offerAcceptanceStatus || "");
        setSavedSchool(data?.offerAcceptanceSchool || "");
      })
      // No saved status yet is reported as a failed fetch — treat it as empty, same as Account.jsx
      .catch(() => {
        setSavedStatus("");
        setSavedSchool("");
      })
      .finally(() => setAcceptanceLoaded(true));
  }, [apiInstance, athleteId]);

  useEffect(() => {
    loadAcceptanceStatus();
  }, [loadAcceptanceStatus]);

  const handleAcceptanceSubmit = async (status, school) => {
    setSavingAcceptance(true);
    // Normalise the "No" sentinel to "" — consistent with Account.jsx and the API contract
    const statusValue = status === "no" ? "" : status;
    const schoolValue = statusValue === "" ? "" : school;
    try {
      await SaveOfferAcceptanceStatus(apiInstance, athleteId, {
        offerAcceptanceStatus: statusValue,
        offerAcceptanceSchool: schoolValue,
      });
      setSavedStatus(statusValue);
      setSavedSchool(schoolValue);
      setAcceptanceModalOpen(false);
      onAcceptanceChange?.(statusValue);
      toast.success("Offer acceptance status saved!");
    } catch {
      toast.error("Failed to save offer acceptance status.");
    } finally {
      setSavingAcceptance(false);
    }
  };

  const handleRemoveConfirmed = async () => {
    setRemoveConfirmOpen(false);
    const prevStatus = savedStatus;
    const prevSchool = savedSchool;
    setSavingAcceptance(true);
    try {
      await SaveOfferAcceptanceStatus(apiInstance, athleteId, {
        offerAcceptanceStatus: "",
        offerAcceptanceSchool: "",
      });
      setSavedStatus("");
      setSavedSchool("");
      onAcceptanceChange?.("");
    } catch {
      setSavedStatus(prevStatus);
      setSavedSchool(prevSchool);
      onAcceptanceChange?.(prevStatus);
      toast.error("Failed to remove offer acceptance status.");
    } finally {
      setSavingAcceptance(false);
    }
  };

  const radiosDisabled = !acceptanceLoaded || savingAcceptance;

  return (
    <>
      <div className="px-6 -mx-6 overflow-y-auto overflow-x-hidden h-[550px]">
        <div className="p-0 text-gray-700 text-sm">
          <div className="flex items-center justify-between p-0">
            <h2 className="font-medium mb-4 uppercase">Offers</h2>
            <div
              onClick={openCreateOffer}
              className="float-right text-sm text-white border border-gray-300 p-2 rounded bg-blue-600 hover:bg-blue-800 cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlus} /> Add new Offer
            </div>
          </div>
          {Array.isArray(offers) && offers.length > 0 ? (
            <div className="grid grid-cols-5 gap-4 border border-blue-700 rounded-md p-4 text-gray-800 items-center mb-5 mt-2">
              {offers.map((offer) => (
                <React.Fragment key={offer.id}>
                  <div>{offer.schoolName}</div>
                  <div>{offer.division}</div>
                  <div className="justify-self-end font-bold">
                    {offer.offerYear}
                  </div>
                  <FontAwesomeIcon
                    icon={faPencil}
                    className="justify-self-end text-blue-700 cursor-pointer"
                    onClick={() => openEditOffer(offer)}
                  />
                  <FontAwesomeIcon
                    icon={faTrash}
                    className="justify-self-end text-red-700 cursor-pointer"
                    onClick={() => openDeleteOffer(offer.id)}
                  />
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No Offers added yet.
            </div>
          )}

          <div className="flex items-center gap-4 mt-2">
            <span className="text-sm font-semibold">
              Have you accepted any offers?
            </span>
            {!acceptanceLoaded && (
              <span className="text-xs text-gray-400 italic">Loading…</span>
            )}
            <label className="flex items-center gap-1 text-sm cursor-pointer">
              <input
                type="radio"
                name="acceptedOffer"
                value="yes"
                checked={acceptanceLoaded && hasAcceptedOffer}
                onChange={() => {}}
                onClick={() => setAcceptanceModalOpen(true)}
                disabled={radiosDisabled}
              />
              Yes
            </label>
            <label className="flex items-center gap-1 text-sm cursor-pointer">
              <input
                type="radio"
                name="acceptedOffer"
                value="no"
                checked={acceptanceLoaded && !hasAcceptedOffer}
                onChange={() => {}}
                onClick={() => {
                  if (hasAcceptedOffer) setRemoveConfirmOpen(true);
                }}
                disabled={radiosDisabled}
              />
              No
            </label>
            {hasAcceptedOffer && (
              <span className="text-sm text-gray-500 italic">
                {savedStatus === "Committed"
                  ? "Verbal commitment"
                  : "Signed LOI"}
                {savedSchool ? ` — ${savedSchool}` : ""}
                <button
                  type="button"
                  className="ml-2 text-blue-600 underline"
                  onClick={() => setAcceptanceModalOpen(true)}
                >
                  Edit
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      <OfferDialog
        open={isOfferOpen}
        onClose={() => setIsOfferOpen(false)}
        isOfferEdit={isOfferEdit}
        handleOfferSubmit={handleOfferSubmit}
        handleOfferSubmission={handleOfferSubmission}
        offerRegister={offerRegister}
        divisions={divisions}
        date={date}
        loading={loading}
        resetOffer={resetOffer}
        setSelectedOffer={setSelectedOffer}
        setIsOfferEdit={setIsOfferEdit}
      />
      <DeleteConfirmationDialog
        open={deleteOfferOpen}
        onClose={() => setDeleteOfferOpen(false)}
        title="Delete Offer?"
        description="Are you sure you want to delete the selected offer?"
        onConfirm={handleOfferDeleteConfirm}
      />
      <DeleteConfirmationDialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        title="Remove offer acceptance?"
        description="This will clear the saved acceptance status. Are you sure?"
        onConfirm={handleRemoveConfirmed}
      />
      <OfferAcceptanceModal
        open={acceptanceModalOpen}
        onClose={() => setAcceptanceModalOpen(false)}
        onSubmit={handleAcceptanceSubmit}
        initialValues={{
          offerAcceptanceStatus: savedStatus || "no",
          offerAcceptanceSchool: savedSchool,
        }}
      />
    </>
  );
}
